import { useId, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { SeoStrategyEditBody, SeoStrategyField, SeoStrategyRecommendation } from "@/types/seo-strategy";

/**
 * The whole-form draft the editing card holds (`components/seo-strategy.md § The screen`, "the
 * draft lives here and dies with it" -- `StrategyCard` owns one of these for the life of the
 * session). `gapTitle` is edited by `StrategyCard`'s own title `Input` (`[A]`); every other key is
 * edited through this file's `StrategyCardEditBody` (`[B]`). Every field is a plain string -- the
 * prefill coalesces every nullable source field to `""` up front, so nothing downstream has to.
 */
export type StrategyEditDraft = {
  gapTitle: string;
  questionText: string;
  consolidatedFinding: string;
  whyItMatters: string;
  evidenceSummary: string;
  hypothesis: string;
  field: SeoStrategyField;
  thematicGroupingName: string;
};

/** The keys `StrategyCardEditBody` itself edits -- everything but the title. */
export type StrategyCardEditBodyField = Exclude<keyof StrategyEditDraft, "gapTitle">;

/**
 * `components/seo-strategy.md § The screen`, "The draft's prefill": `gapTitle`;
 * `questionText ?? sourceQuestions.join("\n")`; the rest of the nullable text fields with
 * `null` -> `""`; `field` with `null` -> `"Title"`; the grouping's name (`""` when there is no
 * grouping).
 */
export function buildStrategyEditDraft(recommendation: SeoStrategyRecommendation): StrategyEditDraft {
  return {
    gapTitle: recommendation.gapTitle ?? "",
    questionText: recommendation.questionText ?? recommendation.sourceQuestions.join("\n"),
    consolidatedFinding: recommendation.consolidatedFinding ?? "",
    whyItMatters: recommendation.whyItMatters ?? "",
    evidenceSummary: recommendation.evidenceSummary ?? "",
    hypothesis: recommendation.hypothesis ?? "",
    field: recommendation.field ?? "Title",
    thematicGroupingName: recommendation.thematicGrouping?.name ?? "",
  };
}

/**
 * "The grouping control is editable only when the grouping is non-null and its served `status` is
 * `suggested`; otherwise it is read-only text -- the name, or `None` -- and
 * `thematicGroupingName` is left out of the body."
 */
export function isStrategyGroupingEditable(recommendation: SeoStrategyRecommendation): boolean {
  return recommendation.thematicGrouping !== null && recommendation.thematicGrouping.status === "suggested";
}

/** "`Save` is disabled while the trimmed title or the trimmed hypothesis is empty." */
export function isStrategyEditSaveDisabled(draft: StrategyEditDraft): boolean {
  return draft.gapTitle.trim() === "" || draft.hypothesis.trim() === "";
}

/**
 * WEB-002's `SeoStrategyEditBody`, built from the draft: "empty optional strings are sent as `""`
 * (the server maps them)" -- so every text field goes through as-is, and `thematicGroupingName` is
 * omitted (not sent as `""`) whenever the grouping is not editable, so an unrelated save can never
 * rename a grouping the user never touched.
 *
 * Also omitted when the grouping IS editable but the draft's name is unchanged from the
 * recommendation's own: `UpdatePrioritizedStrategyService::renamedGrouping` 422s ("This
 * grouping can no longer be renamed.") once the STORED grouping is no longer `suggested`
 * (e.g. added to the Master List from another tab while this card sat open), and a save
 * that never touched the name has no reason to risk that rejection.
 */
export function buildStrategyEditBody(
  draft: StrategyEditDraft,
  recommendation: SeoStrategyRecommendation,
): SeoStrategyEditBody {
  const body: SeoStrategyEditBody = {
    gapTitle: draft.gapTitle,
    hypothesis: draft.hypothesis,
    field: draft.field,
    questionText: draft.questionText,
    consolidatedFinding: draft.consolidatedFinding,
    whyItMatters: draft.whyItMatters,
    evidenceSummary: draft.evidenceSummary,
  };

  const groupingNameChanged = draft.thematicGroupingName !== (recommendation.thematicGrouping?.name ?? "");
  if (isStrategyGroupingEditable(recommendation) && groupingNameChanged) {
    body.thematicGroupingName = draft.thematicGroupingName;
  }

  return body;
}

interface FieldProps {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}

function EditField({ label, htmlFor, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={htmlFor}
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

interface StrategyCardEditBodyProps {
  recommendation: SeoStrategyRecommendation;
  draft: StrategyEditDraft;
  onChange: <K extends StrategyCardEditBodyField>(key: K, value: StrategyEditDraft[K]) => void;
}

/**
 * `[B]` of the editing card (`decisions/the-strategy-card-is-a-shell-with-an-action-slot.md`
 * decision 3, refined by `components/seo-strategy.md § The screen`) -- `StrategyCardBody`'s
 * sibling in edit mode, same two columns and dividers, each value `p` replaced by a labelled
 * control. Fully controlled: `StrategyCard` owns the draft (`gapTitle` included, for the title
 * `Input` in its own header) and passes the slice this component edits down.
 */
export function StrategyCardEditBody({ recommendation, draft, onChange }: StrategyCardEditBodyProps) {
  const groupingEditable = isStrategyGroupingEditable(recommendation);
  // W-2: several cards can be in edit mode at once (WEB-005), so a constant id per
  // control collides across instances -- the second card's <Label htmlFor> then points
  // at the first card's control (`getElementById`/`.labels` resolve to the first match
  // in document order), leaving the second card's own textareas with no accessible name.
  // `useId()` is unique per component instance and stable across renders, so each card's
  // controls get their own ids.
  const idPrefix = useId();
  const questionId = `${idPrefix}-question`;
  const gapId = `${idPrefix}-gap`;
  const whyItMattersId = `${idPrefix}-why-it-matters`;
  const evidenceId = `${idPrefix}-evidence`;
  const hypothesisId = `${idPrefix}-hypothesis`;
  const groupingId = `${idPrefix}-grouping`;

  return (
    <div className="grid gap-6 md:grid-cols-2 md:divide-x md:divide-border">
      <section className="space-y-4 md:pr-6">
        <h5 className="text-base font-semibold leading-6 text-foreground">Opportunity</h5>

        <EditField label="Question" htmlFor={questionId}>
          <Textarea
            id={questionId}
            rows={2}
            value={draft.questionText}
            onChange={(event) => onChange("questionText", event.target.value)}
          />
        </EditField>

        <EditField label="Gap" htmlFor={gapId}>
          <Textarea
            id={gapId}
            rows={3}
            value={draft.consolidatedFinding}
            onChange={(event) => onChange("consolidatedFinding", event.target.value)}
          />
        </EditField>

        <EditField label="Why it matters" htmlFor={whyItMattersId}>
          <Textarea
            id={whyItMattersId}
            rows={3}
            value={draft.whyItMatters}
            onChange={(event) => onChange("whyItMatters", event.target.value)}
          />
        </EditField>

        <EditField label="Evidence" htmlFor={evidenceId}>
          <Textarea
            id={evidenceId}
            rows={3}
            value={draft.evidenceSummary}
            onChange={(event) => onChange("evidenceSummary", event.target.value)}
          />
        </EditField>
      </section>

      <section className="space-y-4 md:pl-6">
        <h5 className="text-base font-semibold leading-6 text-foreground">Recommendation</h5>

        <EditField label="Field">
          <Select value={draft.field} onValueChange={(value) => onChange("field", value as SeoStrategyField)}>
            <SelectTrigger aria-label="Field to test">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Title">Title</SelectItem>
              <SelectItem value="Bullets">Bullets</SelectItem>
            </SelectContent>
          </Select>
        </EditField>

        <EditField label="Hypothesis" htmlFor={hypothesisId}>
          <Textarea
            id={hypothesisId}
            rows={4}
            value={draft.hypothesis}
            onChange={(event) => onChange("hypothesis", event.target.value)}
          />
        </EditField>

        <EditField label="Relevant thematic grouping" htmlFor={groupingEditable ? groupingId : undefined}>
          {groupingEditable ? (
            <Input
              id={groupingId}
              placeholder="None"
              value={draft.thematicGroupingName}
              onChange={(event) => onChange("thematicGroupingName", event.target.value)}
            />
          ) : (
            <p className="text-sm text-foreground">{recommendation.thematicGrouping?.name || "None"}</p>
          )}
        </EditField>
      </section>
    </div>
  );
}
